/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: SaveButtonWithDropdown.tsx
 🗓️ Created: 2025-01-15
 🧩 Description:
    Save button with dropdown menu to select save action:
    - Save and close (return to list)
    - Save and new (stay in form, create new)
    - Save (stay in form, continue editing)
    
    Preference is saved in localStorage and persists across sessions.
────────────────────────────────────────────────────────────────*/

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Save, Loader2, ChevronDown, Check } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';

export type SaveAction = 'save' | 'save-and-close' | 'save-and-new';

interface SaveButtonWithDropdownProps {
  onSave: (action: SaveAction) => void | Promise<void>;
  disabled?: boolean;
  isLoading?: boolean;
  storageKey?: string; // Key for localStorage (e.g., 'conferences:saveAction')
  className?: string;
  availableActions?: SaveAction[]; // Optional: filter available actions (e.g., ['save', 'save-and-close'])
}

interface SaveOption {
  value: SaveAction;
  label: string;
  labelKey: string; // i18n key
}

const SAVE_OPTIONS: SaveOption[] = [
  { value: 'save-and-close', labelKey: 'common.saveAndClose', label: 'Save and close' },
  { value: 'save-and-new', labelKey: 'common.saveAndNew', label: 'Save and new' },
  { value: 'save', labelKey: 'common.save', label: 'Save' },
];

const DEFAULT_ACTION: SaveAction = 'save-and-close';

export const SaveButtonWithDropdown: React.FC<SaveButtonWithDropdownProps> = ({
  onSave,
  disabled = false,
  isLoading = false,
  storageKey = 'default:saveAction',
  className = '',
  availableActions,
}) => {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<SaveAction>(DEFAULT_ACTION);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  
  // Filter options based on availableActions
  const filteredOptions = useMemo(() => {
    return availableActions 
      ? SAVE_OPTIONS.filter(opt => availableActions.includes(opt.value))
      : SAVE_OPTIONS;
  }, [availableActions]);
  
  // Ensure selectedAction is valid for filtered options
  const validSelectedAction = useMemo(() => {
    return filteredOptions.some(opt => opt.value === selectedAction)
      ? selectedAction
      : (filteredOptions[0]?.value || DEFAULT_ACTION);
  }, [filteredOptions, selectedAction]);
  
  // Load saved preference from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved && (saved === 'save' || saved === 'save-and-close' || saved === 'save-and-new')) {
        const savedAction = saved as SaveAction;
        // Only set if it's in the available actions
        if (!availableActions || availableActions.includes(savedAction)) {
          setSelectedAction(savedAction);
        } else {
          // Find first valid action from filtered options
          const firstValid = filteredOptions[0]?.value || DEFAULT_ACTION;
          setSelectedAction(firstValid);
        }
      } else {
        // Find first valid action from filtered options
        const firstValid = filteredOptions[0]?.value || DEFAULT_ACTION;
        setSelectedAction(firstValid);
      }
    } catch (error) {
      console.error('Error loading save action preference:', error);
      const firstValid = filteredOptions[0]?.value || DEFAULT_ACTION;
      setSelectedAction(firstValid);
    }
  }, [storageKey, availableActions, filteredOptions]);

  // Save preference to localStorage
  const savePreference = useCallback(
    (action: SaveAction) => {
      try {
        localStorage.setItem(storageKey, action);
      } catch (error) {
        console.error('Error saving save action preference:', error);
      }
    },
    [storageKey]
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Handle option selection
  const handleSelectOption = useCallback(
    async (action: SaveAction) => {
      setSelectedAction(action);
      savePreference(action);
      setIsOpen(false);
      // Execute save with the selected action
      await onSave(action);
    },
    [savePreference, onSave]
  );

  // Handle save button click
  const handleSaveClick = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      
      // If clicking the dropdown arrow, toggle dropdown (don't save)
      const target = e.target as HTMLElement;
      const isDropdownClick = target.closest('.save-dropdown-arrow') || target.classList.contains('save-dropdown-arrow');
      
      if (isDropdownClick) {
        setIsOpen(!isOpen);
        return;
      }

      // If dropdown is open, don't execute save (let the option selection handle it)
      if (isOpen) {
        return;
      }

      // Otherwise, execute save with selected action
      await onSave(selectedAction);
    },
    [onSave, selectedAction, isOpen]
  );

  const selectedOption = filteredOptions.find((opt) => opt.value === selectedAction) || filteredOptions[0];
  const displayText = selectedOption ? t(selectedOption.labelKey, selectedOption.label) : t('common.save', 'Save');

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Main Save Button with Dropdown */}
      <button
        ref={buttonRef}
        type="button"
        onClick={handleSaveClick}
        disabled={disabled || isLoading}
        className={`
          flex items-center gap-2 min-w-[140px] h-9 px-4
          bg-primary bg-gradient-to-br from-black/30 to-white/10 text-white rounded-lg
          border-0
          hover:scale-105 hover:shadow-lg
          focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
          disabled:opacity-50 disabled:cursor-not-allowed
          transition-colors
          ${isOpen ? 'ring-2 ring-primary ring-offset-2' : ''}
        `}
      >
        {/* Left side - Icon and Text */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
          ) : (
            <Save className="h-4 w-4 flex-shrink-0" />
          )}
          <span className="truncate font-medium">
            {isLoading ? t('common.saving', 'Saving...') : displayText}
          </span>
        </div>

        {/* Right side - Dropdown Arrow */}
        <div
          className={`
            save-dropdown-arrow
            flex items-center justify-center
            h-full w-8 ml-2 -mr-2
            border-l border-white/20
            cursor-pointer
            hover:bg-white/10
            transition-colors
            ${isOpen ? 'bg-white/10' : ''}
          `}
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
        >
          <ChevronDown
            className={`
              h-4 w-4 transition-transform
              ${isOpen ? 'rotate-180' : ''}
            `}
          />
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={`
            absolute bottom-full right-0 mb-2
            bg-popover border border-border rounded-md shadow-lg
            min-w-[180px] z-50
            overflow-hidden
          `}
        >
          {filteredOptions.map((option) => {
            const isSelected = option.value === selectedAction;
            const optionText = t(option.labelKey, option.label);

            return (
              <button
                key={option.value}
                type="button"
                onClick={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  await handleSelectOption(option.value);
                }}
                className={`
                  w-full px-4 py-2 text-sm text-left
                  flex items-center justify-between gap-2
                  hover:bg-accent hover:text-accent-foreground
                  transition-colors
                  ${isSelected ? 'bg-accent/50' : ''}
                `}
              >
                <span>{optionText}</span>
                {isSelected && <Check className="h-4 w-4 text-primary flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SaveButtonWithDropdown;

